// EN mirror of /quote — instant quote landing for foreign clients
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Calculator, MessageCircle, Phone, ShieldCheck, Clock3, FileCheck2 } from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Get a Free Quote — Translation, Notary, Apostille Bangkok | NYC Language Institute";
const DESC =
  "Instant written quote in 15 minutes. Certified translation 50+ languages, notary public. Send your documents for a free eligibility check.";

export const Route = createFileRoute("/en/quote")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/quote", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/quote", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/quote", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/quote", "en")},
      { property: "og:url", content: `${SITE}/en/quote` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/quote` },
      ...hreflangLinks("/quote", "/en/quote"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Free Quote — Translation & Legalization",
        provider: { "@type": "LegalService", name: "NYC Legal & Notary Service Co., Ltd.", url: SITE },
        areaServed: "TH",
        inLanguage: "en",
        offers: { "@type": "Offer", priceCurrency: "THB", price: "0", description: "Free written quote, no obligation" },
      }),
    }],
  }),
  component: EnQuotePage,
});

function EnQuotePage() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-4xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Free written quote in <span className="text-amber-300">15 minutes</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Tell us your document, target language and destination country. We confirm pricing, turnaround and
            acceptance scope in writing — no surprise fees, no obligation.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <LeadFormModal trigger={
              <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-6 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
                <Calculator className="h-4 w-4" /> Request quote
              </button>
            } />
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-[#04A03A] px-6 py-3 text-sm font-bold text-white">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-6 py-3 text-sm font-bold text-white">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-3">
          {[
            { icon: ShieldCheck, t: "Guaranteed acceptance", d: "Embassy, MFA, court & immigration. We re-do for free if rejected." },
            { icon: Clock3, t: "Same-day rush available", d: "Standard 24–48h. Rush 4–8h for an extra fee. 5,400+ jobs/yr." },
            { icon: FileCheck2, t: "End-to-end workflow", d: "Translation → notarisation → MFA → embassy. One invoice, one timeline." },
          ].map((c) => (
            <article key={c.t} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <c.icon className="h-7 w-7 text-amber-600" />
              <h2 className="mt-3 text-base font-bold text-navy-deep">{c.t}</h2>
              <p className="mt-2 text-sm text-slate-600">{c.d}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-10 max-w-5xl rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
          Looking for full contact details, office address or service map? Visit{" "}
          <Link to="/en/contact" className="font-bold text-amber-700 hover:underline">our Contact page</Link>{" "}
          or browse the{" "}
          <Link to="/en/services" className="font-bold text-amber-700 hover:underline">full service catalogue</Link>.
        </div>
      </section>
    <RelatedHubGrid locale="en" /></main>
  );
}
